/**
 * ShatterpointSkillEffect — Grit hurtmore evolution, replaces Force Driver.
 *
 * Extends PhasedSkillEffect. Larger crystalline spike, faster wind-up,
 * 40% more base damage than Force Driver, 0.8s stun on hit. On kill,
 * registers a detonation via DeathTriggerSystem (8 damage, 1.5-tile radius).
 *
 * Inherits Coiled (stunDurationMs), Penetrating Force (pierceDamageMultiplier),
 * and Unrelenting from the hurtmore upgrade chain via enhancesId.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Shatterpoint
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ShatterpointDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
  readonly deathTriggers: {
    register(config: unknown): void;
    unregister(triggerId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ShatterpointConfig {
  readonly windupMs: number;
  readonly activeMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseStunDurationMs: number;
  readonly baseDamage: number;
  readonly detonationDamage: number;
  readonly detonationRadiusTiles: number;
}

const DEFAULT_CONFIG: ShatterpointConfig = {
  windupMs: 150,        // Faster than Force Driver (200ms)
  activeMs: 100,
  recoveryMs: 250,
  cooldownMs: 1200,
  baseStunDurationMs: 800,
  baseDamage: 49,       // Force Driver 35 × 1.4
  detonationDamage: 8,
  detonationRadiusTiles: 1.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ShatterpointSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'hurtmore_evolved';

  protected readonly phaseSequence: readonly SkillPhase[] =
    ['windup', 'active', 'recovery', 'cooldown'];

  private readonly deps: ShatterpointDeps;
  private readonly config: ShatterpointConfig;

  private _resolvedDamage = 0;
  private _resolvedStunMs = 0;
  private _resolvedPierceDamageMultiplier = 0;
  private _stunApplied = false;
  private _stunBlocked = false;
  private _deathTriggerRegistered = false;

  constructor(deps: ShatterpointDeps, config: Partial<ShatterpointConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this.config.activeMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onActivate(): void {
    this._resolvedDamage = 0;
    this._resolvedStunMs = 0;
    this._resolvedPierceDamageMultiplier = 0;
    this._stunApplied = false;
    this._stunBlocked = false;

    // Register kill detonation trigger if not already registered
    if (!this._deathTriggerRegistered) {
      this.deps.deathTriggers.register({
        id: 'shatterpoint_detonation',
        sourceSkillId: 'hurtmore_evolved',
        requireKillBySkill: 'hurtmore_evolved',
        explosion: {
          damage: this.config.detonationDamage,
          radiusTiles: this.config.detonationRadiusTiles,
        },
      });
      this._deathTriggerRegistered = true;
    }
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this.resolveHit();
    }
  }

  private resolveHit(): void {
    // Damage — queries hurtmore upgrades via enhancesId chain
    const damageMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'hurtmore', 'damage',
    );
    this._resolvedDamage = this.config.baseDamage * damageMultiplier;

    // Stun duration — base 800ms + Coiled bonus
    const bonusStunMs = this.deps.resolver.getAdditiveEffect(
      'hurtmore', 'stunDurationMs',
    );
    this._resolvedStunMs = this.config.baseStunDurationMs + bonusStunMs;

    // Check stun immunity
    const isImmune = this.deps.statusEffects.has('stun');
    if (!isImmune) {
      this.deps.statusEffects.apply('stun', this._resolvedStunMs, 'hurtmore_evolved');
      this._stunApplied = true;
      this._stunBlocked = false;
    } else {
      this._stunApplied = false;
      this._stunBlocked = true;
    }

    // Pierce — resolve multiplier for secondary target penetration (inherited from hurtmore chain)
    const pierceMult = this.deps.resolver.getMaxEffect('hurtmore', 'pierceDamageMultiplier');
    this._resolvedPierceDamageMultiplier = pierceMult ?? 0;
    if (this._resolvedPierceDamageMultiplier > 0) {
      // TODO: scene-wiring — find secondary target behind primary and apply pierce damage
      // Pierce damage = this._resolvedDamage * this._resolvedPierceDamageMultiplier
    }

    this.markHitApplied();
  }

  // --- Getters ---

  get resolvedDamage(): number { return this._resolvedDamage; }
  get resolvedStunMs(): number { return this._resolvedStunMs; }
  get resolvedPierceDamageMultiplier(): number { return this._resolvedPierceDamageMultiplier; }
  get stunApplied(): boolean { return this._stunApplied; }
  get stunBlocked(): boolean { return this._stunBlocked; }
  get deathTriggerRegistered(): boolean { return this._deathTriggerRegistered; }
}
